import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import PaywallModal from '../components/PaywallModal'
import { collection, query, where, orderBy, getDocs, getDoc, doc } from 'firebase/firestore'
import { db } from '../utils/firebase'
import { formatPhoneNumber } from '../utils/phone'
import {
  Search,
  Users,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Heart,
  Baby,
  Sparkles,
  Info,
  EyeOff,
  X,
  LayoutGrid,
  List,
  MessageCircle
} from 'lucide-react'
import './DirectoryPage.css'

function DirectoryPage() {
  const navigate = useNavigate()
  const { userProfile, isNeighborHidden, isSubscriptionActive } = useAuth()
  const isAdmin = userProfile?.isAdmin || userProfile?.isBoardMember || userProfile?.isSuperAdmin
  const [residents, setResidents] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  // Grid is the new default in the 2026 refresh.
  const [viewMode, setViewMode] = useState('list')
  const [neighborhoodName, setNeighborhoodName] = useState('')

  const handleMessageClick = (residentId) => {
    navigate(`/messages?startWith=${residentId}`)
  }

  useEffect(() => {
    if (userProfile?.neighborhoodId) {
      loadDirectory()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.neighborhoodId])

  const loadDirectory = async () => {
    try {
      const neighborhoodDoc = await getDoc(doc(db, 'neighborhoods', userProfile.neighborhoodId))
      if (neighborhoodDoc.exists()) {
        setNeighborhoodName(neighborhoodDoc.data().name || userProfile.neighborhoodId)
      }
    } catch (e) {
      console.error('Error loading neighborhood:', e)
    }

    try {
      setLoading(true)
      const usersQuery = query(
        collection(db, 'users'),
        where('neighborhoodId', '==', userProfile.neighborhoodId),
        orderBy('name')
      )

      const snapshot = await getDocs(usersQuery)
      const usersList = snapshot.docs
        .map(d => ({
          id: d.id,
          ...d.data(),
          joinedDate: d.data().joinedDate?.toDate() || new Date()
        }))
        .filter(user => user.showInDirectory !== false)

      setResidents(usersList)
    } catch (error) {
      console.error('Error loading directory:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredResidents = residents
    .filter(r => !isNeighborHidden(r.email))
    .filter(r => {
      if (!searchQuery) return true
      const search = searchQuery.toLowerCase()
      return (
        r.name?.toLowerCase().includes(search) ||
        r.address?.toLowerCase().includes(search)
      )
    })

  const extractStreetAddress = (fullAddress) => {
    if (!fullAddress) return ''
    const parts = fullAddress.split(',')
    if (parts.length <= 1) return fullAddress

    const secondPart = parts[1]?.toLowerCase().trim() || ''
    const isUnit = secondPart.includes('apt') ||
                   secondPart.includes('unit') ||
                   secondPart.includes('suite') ||
                   secondPart.includes('#')

    if (isUnit && parts.length >= 2) {
      return `${parts[0].trim()}, ${parts[1].trim()}`
    }
    return parts[0].trim()
  }

  const getRole = (user) => {
    if (user.isAdmin) {
      const title = (user.adminTitle || '').trim()
      return {
        label: title ? `Admin · ${title}` : 'Admin',
        className: 'badge-admin',
      }
    }
    if (user.isBoardMember) {
      const title = (user.boardTitle || '').trim()
      return {
        label: title ? `Board · ${title}` : 'Board',
        className: 'badge-board',
      }
    }
    return null
  }

  const locationLabel = neighborhoodName || userProfile?.neighborhoodId || 'your neighborhood'
  const count = filteredResidents.length

  return (
    <div className="directory-page">
      {!isSubscriptionActive() && <PaywallModal isAdmin={isAdmin} />}

      {/* Header — matches DiscussionsPage vocabulary. */}
      <header className="directory-header">
        <div className="directory-header-text">
          <h1>Directory</h1>
          <p className="muted">
            {count === 0
              ? `No neighbors match in ${locationLabel}`
              : `${count} ${count === 1 ? 'neighbor' : 'neighbors'} in ${locationLabel}`}
          </p>
        </div>
      </header>

      {/* Filters: search + view toggle in one row */}
      <div className="directory-toolbar">
        <div className="directory-search" data-walk="directory-search">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by name or address"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="directory-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className="segmented" role="tablist" aria-label="View">
          <button
            type="button"
            className={`segmented-item${viewMode === 'grid' ? ' active' : ''}`}
            onClick={() => setViewMode('grid')}
            aria-label="Grid view"
          >
            <LayoutGrid size={16} />
            <span>Grid</span>
          </button>
          <button
            type="button"
            className={`segmented-item${viewMode === 'list' ? ' active' : ''}`}
            onClick={() => setViewMode('list')}
            aria-label="List view"
          >
            <List size={16} />
            <span>List</span>
          </button>
        </div>
      </div>

      {/* Directory Content */}
      {loading ? (
        <div className="directory-loading">
          <div className="spinner"></div>
          <p className="muted">Loading directory…</p>
        </div>
      ) : count === 0 ? (
        <div className="card directory-empty">
          <div className="directory-empty-icon">
            <Users size={32} />
          </div>
          <h3>No neighbors match</h3>
          <p className="muted">
            {searchQuery
              ? 'Try a different name or street.'
              : 'No one has joined the directory yet.'}
          </p>
          {searchQuery && (
            <button className="btn-outline" onClick={() => setSearchQuery('')}>
              Clear search
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="directory-grid">
          {filteredResidents.map((resident, idx) => (
            <ResidentCard
              key={resident.id}
              resident={resident}
              formatPhoneNumber={formatPhoneNumber}
              extractStreetAddress={extractStreetAddress}
              getRole={getRole}
              onMessageClick={handleMessageClick}
              currentUserId={userProfile?.uid}
              isFirst={idx === 0}
            />
          ))}
        </div>
      ) : (
        <div className="directory-list">
          {filteredResidents.map((resident, idx) => (
            <ResidentRow
              key={resident.id}
              resident={resident}
              formatPhoneNumber={formatPhoneNumber}
              extractStreetAddress={extractStreetAddress}
              getRole={getRole}
              onMessageClick={handleMessageClick}
              currentUserId={userProfile?.uid}
              isFirst={idx === 0}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// Small info icon (+ hover popover) shown next to a resident's name
// when they've filled in optional bio fields. Preserved from the
// previous design.
function BioHover({ resident }) {
  const occupation = (resident.occupation || '').trim()
  const partner = (resident.partner || '').trim()
  const children = (resident.children || '').trim()
  const hobbies = (resident.hobbies || '').trim()
  if (!occupation && !partner && !children && !hobbies) return null

  const [openTouch, setOpenTouch] = React.useState(false)

  return (
    <span
      className={`bio-hover ${openTouch ? 'bio-hover-open' : ''}`}
      onClick={(e) => {
        e.stopPropagation()
        setOpenTouch((v) => !v)
      }}
      onMouseLeave={() => setOpenTouch(false)}
      aria-label={`More about ${resident.name}`}
      tabIndex={0}
    >
      <Info size={14} />
      <span className="bio-popover" role="tooltip">
        <span className="bio-popover-header">About {resident.name}</span>
        {occupation && (
          <span className="bio-popover-row">
            <Briefcase size={13} />
            <span><strong>Occupation:</strong> {occupation}</span>
          </span>
        )}
        {partner && (
          <span className="bio-popover-row">
            <Heart size={13} />
            <span><strong>Partner:</strong> {partner}</span>
          </span>
        )}
        {children && (
          <span className="bio-popover-row">
            <Baby size={13} />
            <span><strong>Children:</strong> {children}</span>
          </span>
        )}
        {hobbies && (
          <span className="bio-popover-row">
            <Sparkles size={13} />
            <span><strong>Hobbies:</strong> {hobbies}</span>
          </span>
        )}
      </span>
    </span>
  )
}

// Grid card — avatar-forward. Circular xl avatar on top, name + role,
// muted address, then a compact contact strip at the bottom that honors
// the resident's showEmail / showPhone privacy flags.
function ResidentCard({ resident, formatPhoneNumber, extractStreetAddress, getRole, onMessageClick, currentUserId, isFirst }) {
  const role = getRole(resident)
  const isCurrentUser = resident.id === currentUserId
  const initial = resident.name?.charAt(0).toUpperCase() || 'U'

  const showEmail = resident.showEmail !== false && resident.emailVerified === true && resident.email
  const showPhone = resident.showPhone !== false && resident.phone

  const handleCardClick = () => {
    if (!isCurrentUser) onMessageClick(resident.id)
  }

  return (
    <div className="card interactive resident-card" onClick={handleCardClick}>
      <div className="avatar xl resident-card-avatar" aria-hidden="true">
        {resident.profilePhotoURL ? (
          <img src={resident.profilePhotoURL} alt="" />
        ) : (
          initial
        )}
      </div>

      <div className="resident-card-name">
        <h3>{resident.name}</h3>
        <BioHover resident={resident} />
      </div>

      {role && (
        <span className={`badge ${role.className} resident-card-role`}>
          {role.label}
        </span>
      )}

      <p className="muted resident-card-address">
        <MapPin size={14} />
        {extractStreetAddress(resident.address)}
      </p>

      <div className="resident-card-contact" onClick={(e) => e.stopPropagation()}>
        {showPhone ? (
          <a
            className="contact-chip"
            href={`tel:${resident.phone}`}
            title={formatPhoneNumber(resident.phone)}
            aria-label={`Call ${resident.name}`}
          >
            <Phone size={16} />
          </a>
        ) : null}
        {showEmail ? (
          <a
            className="contact-chip"
            href={`mailto:${resident.email}`}
            title={resident.email}
            aria-label={`Email ${resident.name}`}
          >
            <Mail size={16} />
          </a>
        ) : null}
        {!isCurrentUser && (
          <button
            type="button"
            className="contact-chip contact-chip-primary"
            onClick={() => onMessageClick(resident.id)}
            title={`Message ${resident.name}`}
            aria-label={`Message ${resident.name}`}
            data-walk={isFirst ? 'directory-message-btn' : undefined}
          >
            <MessageCircle size={16} />
          </button>
        )}
      </div>
    </div>
  )
}

// List row — horizontal `.card.interactive` variant. Avatar left, name
// + address middle, contact icons right.
function ResidentRow({ resident, formatPhoneNumber, extractStreetAddress, getRole, onMessageClick, currentUserId, isFirst }) {
  const role = getRole(resident)
  const isCurrentUser = resident.id === currentUserId
  const initial = resident.name?.charAt(0).toUpperCase() || 'U'

  const showEmail = resident.showEmail !== false && resident.emailVerified === true && resident.email
  const showPhone = resident.showPhone !== false && resident.phone

  const handleRowClick = () => {
    if (!isCurrentUser) onMessageClick(resident.id)
  }

  return (
    <div className="card interactive resident-row" onClick={handleRowClick}>
      <div className="avatar lg" aria-hidden="true">
        {resident.profilePhotoURL ? (
          <img src={resident.profilePhotoURL} alt="" />
        ) : (
          initial
        )}
      </div>

      <div className="resident-row-main">
        <div className="resident-row-nameline">
          <span className="resident-row-name">{resident.name}</span>
          <BioHover resident={resident} />
          {role && (
            <span className={`badge ${role.className}`}>{role.label}</span>
          )}
        </div>
        <div className="resident-row-meta">
          <MapPin size={14} />
          <span>{extractStreetAddress(resident.address)}</span>
          {showEmail && (
            <>
              <span className="dot">·</span>
              <span className="ellipsize">{resident.email}</span>
            </>
          )}
          {showPhone && (
            <>
              <span className="dot">·</span>
              <span>{formatPhoneNumber(resident.phone)}</span>
            </>
          )}
          {!showEmail && !showPhone && (
            <>
              <span className="dot">·</span>
              <span className="hidden-info"><EyeOff size={13} /> Contact hidden</span>
            </>
          )}
        </div>
      </div>

      <div className="resident-row-actions" onClick={(e) => e.stopPropagation()}>
        {showPhone && (
          <a
            className="contact-chip"
            href={`tel:${resident.phone}`}
            title={formatPhoneNumber(resident.phone)}
            aria-label={`Call ${resident.name}`}
          >
            <Phone size={16} />
          </a>
        )}
        {showEmail && (
          <a
            className="contact-chip"
            href={`mailto:${resident.email}`}
            title={resident.email}
            aria-label={`Email ${resident.name}`}
          >
            <Mail size={16} />
          </a>
        )}
        {!isCurrentUser && (
          <button
            type="button"
            className="contact-chip contact-chip-primary"
            onClick={() => onMessageClick(resident.id)}
            title={`Message ${resident.name}`}
            aria-label={`Message ${resident.name}`}
            data-walk={isFirst ? 'directory-message-btn' : undefined}
          >
            <MessageCircle size={16} />
          </button>
        )}
      </div>
    </div>
  )
}

export default DirectoryPage
